/**
 * 组节点 —— 纯逻辑。
 *
 * 组是**一次性封装的子图**：把一堆节点收进一个节点里，用来整理。
 * 它和循环节点共用 ⑥ 那套子图地基。
 *
 * ## 接口：两边由不同的人决定
 *
 * 子图里固定有两个**不可删、不可建**的节点：`__group_in` / `__group_out`。
 *
 * ```
 *   ┌─ 组节点（外面看到的） ──────────────────────┐
 *   │  ● 输入   ← 变参行，数量由**外面**加        │
 *   │  ● 结果 1 ← 数量由**里面**接出来多少决定    │
 *   │  ● 结果 2                                   │
 *   └─────────────────────────────────────────────┘
 * ```
 *
 * | 方向 | 谁决定数量 | 怎么同步 |
 * |---|---|---|
 * | 输入 | **外部**（用户在外面点「＋」加参数） | 内部输入节点把它逐个展开成一行**输出** |
 * | 输出 | **内部**（用户在里面接线） | 外部按内部接了几路生成对应数量的输出行 |
 *
 * 这个不对称是有道理的：
 *   · 输入是「我要往里喂什么」—— 只有用的人知道
 *   · 输出是「我算出了什么」—— 只有里面知道
 *
 * ★ **数据的源头在里面，外面是投影。**
 *
 * 所以「加一路参数」这个动作发生在**组内部**的输入节点上（它有＋/✕），
 * 外面那个组节点上的端口是**镜像**、数量跟着里面走。
 *
 * ⚠️ 方向反过来（曾经就是这样）会出问题：外面连一根线就能改掉组的
 * 内部结构，于是「进组编辑一下」和「外面多接一根线」会互相打架 ——
 * 用户的原话是「防止因为外部改变组内部的结构」。
 */
import type {
  GraphNodeModel,
  InputRow,
  NodeRow,
  OutputRow,
  RepeaterRow,
  SubGraph,
} from '../types/node'
import type { SocketType } from '../types/socket'
import { repeaterCount } from './rows'
/* ⚠️ 子图接口节点的类型名单只有一份（在 subgraph.ts）——别在这里再抄一遍 */
import { isSubgraphInterfaceType } from './subgraph'
import {
  GROUP_INPUT_NODE,
  GROUP_IN_REPEATER,
  GROUP_OUTPUT_NODE,
  GROUP_OUT_REPEATER,
  groupInputRows,
  groupOutputRows,
} from './groupInterface'

/*
 * ★ 接口那一半（声明 / 镜像 / 进出刷新）**已向下拆到** `./groupInterface.ts`。
 *
 * ⚠️ 这里**继续 re-export** 它的名字 —— 既有三处引用
 * （`Playground.vue` / `useGrouping.ts` / `useSubgraphLevels.ts`）
 * 一行都不用改。以后新增的代码请直接 import `./groupInterface`。
 */
export * from './groupInterface'

export interface GroupPort {
  id: string
  label: string
  type: SocketType
}

export interface GroupInterface {
  inputs: GroupPort[]
  outputs: GroupPort[]
}

export type EdgeCrossing = 'inside' | 'outgoing' | 'incoming' | 'outside'

/**
 * 这条边跨不跨边界。
 *
 *   inside    两端都在选区里 → 原样留在子图里
 *   outgoing  里面 → 外面     → 变成组的一个**输出**端口
 *   incoming  外面 → 里面     → 变成组的一个**输入**端口
 *   outside   两端都在外面   → 跟组无关，原样留着
 */
export function classifyEdge(
  edge: { source: string; target: string },
  selected: ReadonlySet<string>,
): EdgeCrossing {
  const sourceIn = selected.has(edge.source)
  const targetIn = selected.has(edge.target)
  if (sourceIn && targetIn) return 'inside'
  if (sourceIn) return 'outgoing'
  if (targetIn) return 'incoming'
  return 'outside'
}

export interface SocketLookup {
  (
    nodeId: string,
    handleId: string | null | undefined,
  ): { type: SocketType; label?: string } | undefined
}

export interface GroupEdge {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

export interface GroupPlan {
  interface: GroupInterface
  innerNodes: GraphNodeModel[]
  innerEdges: GroupEdge[]
  outerEdges: GroupEdge[]
  consumed: GroupEdge[]
}

export interface GroupPlanInput {
  selected: readonly string[]
  edges: readonly GroupEdge[]
  modelOf: (id: string) => GraphNodeModel | undefined
  socketOf: SocketLookup
}

/**
 * 选中的一堆 id 里，**哪些能被收进组**。
 *
 * 排除三类，每一类都有理由：
 *
 * ```text
 *   editorOnly（框 / 注释 / 接通…）  它们是**画布构造**，不是计算节点。
 *                                    收进组会让"整理画布"变得别扭：框的成员
 *                                    关系是另一套（`frameFor`），会和组关系打架。
 *   组的接口节点（__group_in/out）    它们属于**组自己**，只该在组内部出现；
 *                                    收到别的组里会让接口的镜像关系断掉。
 *   模型查不到的 id                  选区里有幽灵（比如刚被别的操作删掉了）。
 * ```
 *
 * ⚠️ **组本身（`type === 'group'`）是允许的** —— 也就是"组里套组"。
 * 这一条原来**被排除**（一个没有注释的 `model.type !== 'group'` 混在上面
 * 那串条件里），后果是：选中两个组、点「建组」，得到一句
 * "先选中要收进组的节点" —— 真机反馈过：「选中两个组节点，不能创建组」。
 *
 * 子图本来就是递归的（`useSubgraphLevels` 有层级面包屑、序列化的
 * `pickSubGraph` 也是递归的），嵌套没有额外的机制成本；
 * 而"组不能进组"是一条**说不清理由**的限制。
 *
 * ⚠️ 抽成纯函数是为了**能被单测**：这条规则埋在宿主的闭包里时，
 * 「选两个组会怎样」只能靠手动点 —— 而它已经被漏掉过一次。
 */
export function groupableIds(
  models: readonly GraphNodeModel[],
  selected: readonly string[],
): string[] {
  const byId = new Map(models.map((model) => [model.id, model]))
  return selected.filter((id) => {
    const model = byId.get(id)
    if (!model) return false
    if (model.editorOnly) return false
    /*
     * ⚠️ 按**类型**判，不是按 id 常量 —— 这里原来写的是
     * `model.type === GROUP_INPUT_NODE`，而那两个常量是 **id**
     * （`'__group_in'`），type 是 `'group.input'`：条件永远不成立，
     * 也就是这条排除**从来没生效过**（真机上一选中接口节点就能收进组）。
     */
    if (isSubgraphInterfaceType(model.type)) return false
    if (model.id === GROUP_INPUT_NODE || model.id === GROUP_OUTPUT_NODE) return false
    return true
  })
}

/**
 * 规划一次「用选中的节点建组」。
 *
 * 跨边界的边会**变成组端口** —— 这是关键：不这么做的话，
 * 建完组外面的线全断了，用户得手动重接一遍。
 *
 * 端口顺序按遇到边的顺序，去重按「外侧端口」—— 同一个外面端口
 * 连进选区里的多个节点时，只开一个组端口，内层分叉。
 */
export function planGrouping(input: GroupPlanInput): GroupPlan {
  const selected = new Set(input.selected)
  const innerNodes = input.selected.flatMap((id) => {
    const model = input.modelOf(id)
    return model ? [model] : []
  })

  const innerEdges: GroupEdge[] = []
  const outerEdges: GroupEdge[] = []
  const consumed: GroupEdge[] = []

  const inputs: GroupPort[] = []
  const outputs: GroupPort[] = []
  const inputByOuter = new Map<string, string>()
  const outputByOuter = new Map<string, string>()
  /** 外层边按**端口**去重：同一个外部端口连进多个内部节点时只补一条 */
  const outerEdgeSeen = new Set<string>()

  for (const edge of input.edges) {
    const crossing = classifyEdge(edge, selected)
    if (crossing === 'inside') {
      innerEdges.push(edge)
      continue
    }
    if (crossing === 'outside') continue

    if (crossing === 'incoming') {
      const outerKey = `${edge.source}.${edge.sourceHandle}`
      let index = inputs.findIndex((port) => port.id === inputByOuter.get(outerKey))
      if (index < 0) {
        const socket = input.socketOf(edge.source, edge.sourceHandle)
        const inner = input.socketOf(edge.target, edge.targetHandle)
        const target = input.modelOf(edge.target)
        index = inputs.length
        inputs.push({
          id: `${GROUP_IN_REPEATER}.${index}`,
          label:
            inner?.label ??
            socket?.label ??
            `${target?.title ?? edge.target}.${edge.targetHandle}`,
          type: socket?.type ?? inner?.type ?? 'any',
        })
        inputByOuter.set(outerKey, `${GROUP_IN_REPEATER}.${index}`)
      }

      innerEdges.push({
        source: GROUP_INPUT_NODE,
        sourceHandle: `${GROUP_IN_REPEATER}.${index}`,
        target: edge.target,
        targetHandle: edge.targetHandle,
      })

      const seen = `in:${outerKey}`
      if (!outerEdgeSeen.has(seen)) {
        outerEdgeSeen.add(seen)
        outerEdges.push({
          source: edge.source,
          sourceHandle: edge.sourceHandle,
          target: '',
          targetHandle: `${GROUP_IN_REPEATER}.${index}`,
        })
      }
      consumed.push(edge)
      continue
    }

    // outgoing：里面 → 外面
    const outerKey = `${edge.target}.${edge.targetHandle}`
    let index = outputs.findIndex((port) => port.id === outputByOuter.get(outerKey))
    if (index < 0) {
      const socket = input.socketOf(edge.target, edge.targetHandle)
      const source = input.modelOf(edge.source)
      const sourceSocket = input.socketOf(edge.source, edge.sourceHandle)
      index = outputs.length
      outputs.push({
        id: `out${index}`,
        label:
          sourceSocket?.label ??
          source?.title ??
          `${source?.title ?? edge.source}.${edge.sourceHandle}`,
        type: socket?.type ?? sourceSocket?.type ?? 'any',
      })
      outputByOuter.set(outerKey, `out${index}`)
    }

    innerEdges.push({
      source: edge.source,
      sourceHandle: edge.sourceHandle,
      target: GROUP_OUTPUT_NODE,
      targetHandle: `${GROUP_OUT_REPEATER}.${index}`,
    })

    const seen = `out:${index}.${outerKey}`
    if (!outerEdgeSeen.has(seen)) {
      outerEdgeSeen.add(seen)
      outerEdges.push({
        source: '',
        sourceHandle: `out${index}`,
        target: edge.target,
        targetHandle: edge.targetHandle,
      })
    }
    consumed.push(edge)
  }

  return {
    interface: { inputs, outputs },
    innerNodes: [
      ...innerNodes,
      {
        id: GROUP_INPUT_NODE,
        type: 'group.input',
        title: '输入',
        category: 'utility',
        rows: groupInputRows(inputs.length),
      },
      {
        id: GROUP_OUTPUT_NODE,
        type: 'group.output',
        title: '输出',
        category: 'utility',
        rows: groupOutputRows(),
      },
    ],
    innerEdges,
    outerEdges,
    consumed,
  }
}

/* ══════════════════════════════════════════════════════════════
   解组（把组摊回父图）
   ══════════════════════════════════════════════════════════════ */

/** 解组要往父图里注入的东西 */
export interface UngroupPlan {
  /** 内层真实节点（已去掉两个接口节点，带新 id） */
  nodes: GraphNodeModel[]
  /** 内层节点之间的边 + 跨越边界的重接（都用新 id） */
  edges: { source: string; sourceHandle: string; target: string; targetHandle: string }[]
  /** 里面什么都没接（摊出来是空的一片） */
  empty: boolean
}

/**
 * 把 id 重命名，避开父图里已有的 id。
 *
 * ⚠️ 前缀用**组自己的 id**：内层 id 只在**本层**唯一（契约里写着），
 * 摊上来可能和父图里任何一个节点撞名 —— 撞了就是两条边指向同一个节点，
 * 而且**看不出来**（画面上就是「我明明有两个节点」）。
 * 用「父 id」做前缀还顺带保留了来源信息，调试时一眼知道是从哪个组出来的。
 */
function renamer(parentIds: ReadonlySet<string>, groupId: string) {
  const map = new Map<string, string>()
  const take = (innerId: string): string => {
    const hit = map.get(innerId)
    if (hit) return hit
    let candidate = `${groupId}/${innerId}`
    let n = 1
    while (parentIds.has(candidate)) candidate = `${groupId}/${innerId}~${n++}`
    map.set(innerId, candidate)
    return candidate
  }
  return take
}

/**
 * 规划一次「解组」：组的内部子图摊回父图，跨越边界的边就地重接。
 *
 * ## 语义（和建组互为逆运算）
 *
 * ```
 *             ┌─ 组 ─────────┐
 *   甲 ───in.0──▶ ● 参数 1      │       甲 ───────────────▶ [里面的那个节点]
 *             │   ● 结果 1 ─out0 ──▶ 乙       [里面的节点] ──▶ 乙
 *             └──────────────┘
 * ```
 *
 * * 外面接在 `in.{i}` 上的线 → 接到**里面那条 `in.{i}` 边**原来指向的节点上；
 * * 里面喂给 `out.{i}` 的线 → 接到外面原来挂在 `out{i}` 上的那些目标上；
 * * 内层两个接口节点（`__group_in` / `__group_out`）**丢掉** —— 它们只是投影。
 *
 * ## 一个外部端口接进多个内部节点时：扇出照搬
 *
 * 建组时同一个外部端口连进选区里多个节点，只开**一个**组端口、内层分叉。
 * 解组要反过来：那个外部端口有几条内层边，就补几条边 ——
 * 否则解组之后里面有节点「少了一根线」，而且没有任何提示。
 */
export function planUngroup(options: {
  graph: SubGraph
  groupId: string
  /** 父图里**已有**的节点 id（用来避开撞名） */
  parentIds: readonly string[]
  /** 父图里的边；`target`/`source` 命中的就在这里换成内层节点 */
  outerEdges: readonly {
    source: string
    sourceHandle?: string | null
    target: string
    targetHandle?: string | null
  }[]
}): UngroupPlan {
  const { graph, groupId, parentIds, outerEdges } = options
  const take = renamer(new Set(parentIds), groupId)

  const interfaceNodes = new Set([GROUP_INPUT_NODE, GROUP_OUTPUT_NODE])
  const nodes = graph.nodes
    .filter((n) => !interfaceNodes.has(n.id))
    .map((n) => ({ ...n, id: take(n.id) }))

  const edges: UngroupPlan['edges'] = []
  const isInterface = (id: string) => interfaceNodes.has(id)

  /**
   * 补一条边。
   *
   * ⚠️ **哪一端在外面，必须由调用方说**（`outside`）——
   * 「外面那一端用**它自己的 id**」和「里面那一端要**改名**」是两件事，
   * 而两者都是普通节点 id，光看字符串分不出来。
   *
   * 第一版没这个参数，于是外面的 `甲` 也被当成内层 id 去改名，
   * 补出来的边变成 `G/甲 → G/A` —— 接到一个**不存在的节点**上。
   */
  const push = (
    source: string,
    sourceHandle: string | null | undefined,
    target: string,
    targetHandle: string | null | undefined,
    outside: { source?: string; target?: string },
  ) => {
    const from = outside.source ?? (isInterface(source) ? undefined : take(source))
    const to = outside.target ?? (isInterface(target) ? undefined : take(target))
    /* 解析不出来（接口节点那侧在外面根本没接线）→ 不补 */
    if (!from || !to) return
    edges.push({
      source: from,
      sourceHandle: sourceHandle ?? '',
      target: to,
      targetHandle: targetHandle ?? '',
    })
  }

  /* ① 内层自己的边（两端都在里面，都要改名） */
  for (const edge of graph.edges) {
    push(edge.source, edge.sourceHandle, edge.target, edge.targetHandle, {})
  }

  /*
   * ② 跨越边界：外面那条边的一端是**组节点自己**，把它换成内层那一端。
   *    · 外面 → 组的参数口 (`in.{i}`)：沿内层那条 `in.{i}` 边往下找
   *    · 组的某个结果口 (`out{i}`) → 外面：沿内层那条 `out.{i}` 边往上找
   */
  for (const edge of outerEdges) {
    if (edge.target === groupId) {
      const handle = edge.targetHandle ?? ''
      for (const inner of graph.edges) {
        if (inner.source !== GROUP_INPUT_NODE || inner.sourceHandle !== handle) continue
        /* 外面那一端是真实的；里面那一端按内层规则改名 */
        push(edge.source, edge.sourceHandle, inner.target, inner.targetHandle, {
          source: edge.source,
        })
      }
      continue
    }
    if (edge.source !== groupId) continue

    /* `out{i}` → 内层的 `out.{i}`（两边 id 形状不同，按序号对应） */
    const index = Number.parseInt((edge.sourceHandle ?? '').replace(/^out/, ''), 10)
    if (!Number.isFinite(index)) continue
    const innerHandle = `${GROUP_OUT_REPEATER}.${index}`
    for (const inner of graph.edges) {
      if (inner.target !== GROUP_OUTPUT_NODE || inner.targetHandle !== innerHandle) continue
      push(inner.source, inner.sourceHandle, edge.target, edge.targetHandle, {
        target: edge.target,
      })
    }
  }

  return { nodes, edges, empty: nodes.length === 0 }
}
